/**
 * Copyright 2025 MadeByTatwa LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * Enhanced confidence scoring system with weighted factors
 */

export interface ConfidenceFactors {
  /** Framework-specific dependency detected (e.g., next, vite) */
  frameworkDependency?: number;
  /** Config file exists (e.g., vite.config.js) */
  configFileExists?: number;
  /** Valid script in package.json */
  scriptExists?: number;
  /** Entry point file exists */
  entryPointExists?: number;
  /** Package.json exists */
  packageJsonExists?: number;
  /** Lock file exists */
  lockFileExists?: number;
  /** Multiple indicators match */
  multipleIndicators?: number;
}

/**
 * Default weights for confidence factors
 */
const DEFAULT_WEIGHTS: Required<ConfidenceFactors> = {
  frameworkDependency: 40,
  configFileExists: 25,
  scriptExists: 15,
  entryPointExists: 10,
  packageJsonExists: 5,
  lockFileExists: 3,
  multipleIndicators: 2,
};

/**
 * Calculate confidence score based on multiple factors
 */
export const calculateConfidence = (
  factors: ConfidenceFactors,
  weights: Partial<ConfidenceFactors> = {}
): number => {
  const finalWeights = { ...DEFAULT_WEIGHTS, ...weights };
  let score = 0;

  // Add scores for each present factor
  Object.entries(factors).forEach(([key, value]) => {
    if (value && key in finalWeights) {
      score += finalWeights[key as keyof ConfidenceFactors];
    }
  });

  // Cap at 100
  return Math.min(score, 100);
};

/**
 * Get confidence level description
 */
export const getConfidenceLevel = (confidence: number): string => {
  if (confidence >= 90) return 'Very High';
  if (confidence >= 75) return 'High';
  if (confidence >= 60) return 'Medium';
  if (confidence >= 40) return 'Low';
  return 'Very Low';
};

/**
 * Determine if confidence is high enough to auto-execute
 */
export const isHighConfidence = (confidence: number, threshold = 75): boolean => {
  return confidence >= threshold;
};

/**
 * Compare two confidence scores and return the better one
 */
export const comparConfidence = (a: number, b: number): number => {
  return a > b ? 1 : a < b ? -1 : 0;
};

/**
 * Boost confidence based on additional context
 */
export const boostConfidence = (
  baseConfidence: number,
  boost: number,
  maxBoost = 10
): number => {
  const actualBoost = Math.min(boost, maxBoost);
  return Math.min(baseConfidence + actualBoost, 100);
};

/**
 * Penalty for missing critical components
 */
export const applyPenalty = (
  baseConfidence: number,
  penalty: number,
  maxPenalty = 20
): number => {
  const actualPenalty = Math.min(penalty, maxPenalty);
  return Math.max(baseConfidence - actualPenalty, 0);
};

